import type { POSCredentials, POSCredentialsPreview, POSIntegration, POSProvider } from '@/services/api';

export const POS_PROVIDER_REMARIS = 'remaris'
export const POS_PROVIDER_SERVUS = 'servus'

export const FALLBACK_POS_PROVIDERS: POSProvider[] = [
  {
    code: 'remaris',
    name: 'Remaris',
    description: 'Remaris Cloud SEO (Standard External Orders)',
  },
  {
    code: 'servus',
    name: 'Servus',
    description: 'Servus QR API (meni + slanje narudžbi)',
  },
]

export const REMARIS_CREDENTIAL_KEYS = {
  apiBaseUrl: 'api_base_url',
  gmApiKey: 'gm_api_key',
  registerId: 'register_id',
  systemName: 'system_name',
} as const

export const SERVUS_CREDENTIAL_KEYS = {
  apiBaseUrl: 'api_base_url',
  username: 'username',
  password: 'password',
  clientUid: 'client_uid',
} as const

export type ProviderFieldDef = {
  key: string
  labelKey: string
  helpKey: string
  required: boolean
  sensitive?: boolean
}

export const REMARIS_FIELD_DEFS: ProviderFieldDef[] = [
  {
    key: REMARIS_CREDENTIAL_KEYS.apiBaseUrl,
    labelKey: 'pos.remarisApiBaseUrl',
    helpKey: 'pos.remarisApiBaseUrlHelp',
    required: true,
  },
  {
    key: REMARIS_CREDENTIAL_KEYS.gmApiKey,
    labelKey: 'pos.remarisGmApiKey',
    helpKey: 'pos.remarisGmApiKeyHelp',
    required: true,
    sensitive: true,
  },
  {
    key: REMARIS_CREDENTIAL_KEYS.registerId,
    labelKey: 'pos.remarisRegisterId',
    helpKey: 'pos.remarisRegisterIdHelp',
    required: true,
  },
  {
    key: REMARIS_CREDENTIAL_KEYS.systemName,
    labelKey: 'pos.remarisSystemName',
    helpKey: 'pos.remarisSystemNameHelp',
    required: false,
  },
]

export const SERVUS_FIELD_DEFS: ProviderFieldDef[] = [
  {
    key: SERVUS_CREDENTIAL_KEYS.apiBaseUrl,
    labelKey: 'pos.servusApiBaseUrl',
    helpKey: 'pos.servusApiBaseUrlHelp',
    required: false,
  },
  {
    key: SERVUS_CREDENTIAL_KEYS.username,
    labelKey: 'pos.servusUsername',
    helpKey: 'pos.servusUsernameHelp',
    required: true,
  },
  {
    key: SERVUS_CREDENTIAL_KEYS.password,
    labelKey: 'pos.servusPassword',
    helpKey: 'pos.servusPasswordHelp',
    required: true,
    sensitive: true,
  },
  {
    key: SERVUS_CREDENTIAL_KEYS.clientUid,
    labelKey: 'pos.servusClientUid',
    helpKey: 'pos.servusClientUidHelp',
    required: true,
  },
]

export function normalizeProviderCode(code: string): string {
  return code.trim().toLowerCase()
}

export function isRemarisProvider(code: string): boolean {
  return normalizeProviderCode(code) === POS_PROVIDER_REMARIS
}

export function isServusProvider(code: string): boolean {
  return normalizeProviderCode(code) === POS_PROVIDER_SERVUS
}

export function getProviderFieldDefs(code: string): ProviderFieldDef[] | null {
  if (isRemarisProvider(code)) return REMARIS_FIELD_DEFS
  if (isServusProvider(code)) return SERVUS_FIELD_DEFS
  return null
}

export function emptyRemarisCredentials(): POSCredentials {
  const out: POSCredentials = {}
  for (const f of REMARIS_FIELD_DEFS) {
    out[f.key] = ''
  }
  return out
}

export function emptyServusCredentials(): POSCredentials {
  const out: POSCredentials = {}
  for (const f of SERVUS_FIELD_DEFS) {
    out[f.key] = f.key === SERVUS_CREDENTIAL_KEYS.apiBaseUrl
      ? 'https://QROrder.servus-app.com:44500'
      : ''
  }
  return out
}

export function emptyCredentialsForProvider(code: string): POSCredentials {
  if (isRemarisProvider(code)) return emptyRemarisCredentials()
  if (isServusProvider(code)) return emptyServusCredentials()
  return {}
}

export function applyCredentialsPreview(
  credentials: POSCredentials,
  preview?: POSCredentialsPreview | null,
): POSCredentials {
  const out = { ...credentials }
  if (!preview?.values) return out
  for (const [key, value] of Object.entries(preview.values)) {
    if (value) out[key] = value
  }
  return out
}

export function resolveCredentialsForForm(
  code: string,
  integration: Pick<POSIntegration, 'credentials' | 'credentials_preview'>,
): POSCredentials {
  const base = emptyCredentialsForProvider(code)
  const stored = integration.credentials
  if (stored && Object.keys(stored).length > 0) {
    return { ...base, ...stored }
  }
  return applyCredentialsPreview(base, integration.credentials_preview)
}

export function getStoredCredentialValue(
  integration: Pick<POSIntegration, 'credentials' | 'credentials_preview'> | null | undefined,
  key: string,
): string {
  return String(integration?.credentials?.[key] ?? integration?.credentials_preview?.values?.[key] ?? '').trim()
}

export function normalizeRemarisApiBaseUrl(raw: string): string {
  const seoPath = '/api/standardExternalOrder/current-pos-data'
  let base = raw.trim()
  if (!base) return base
  if (base.includes(seoPath)) {
    base = base.slice(0, base.indexOf(seoPath)).replace(/\/+$/, '')
  }
  if (!/^https?:\/\//i.test(base)) {
    base = `https://${base.replace(/^\/+/, '')}`
  }
  return base.replace(/\/+$/, '')
}

export function buildCredentialsPayloadForUpdate(
  code: string,
  formCredentials: POSCredentials,
  stored?: POSCredentials | null,
): POSCredentials | null {
  const defs = getProviderFieldDefs(code)
  if (!defs) return null
  const storedCreds = stored ?? {}
  const out: POSCredentials = {}
  for (const f of defs) {
    const formVal = String(formCredentials[f.key] ?? '').trim()
    const storedVal = String(storedCreds[f.key] ?? '').trim()
    let value: string
    if (f.sensitive && !formVal) {

      value = storedVal
    } else if (!f.required && !formVal) {

      value = ''
    } else {
      value = formVal || storedVal
    }
    if (f.key === REMARIS_CREDENTIAL_KEYS.apiBaseUrl && value) {
      value = normalizeRemarisApiBaseUrl(value)
    }
    out[f.key] = value
  }
  for (const f of defs) {
    if (f.required && !out[f.key]) return null
  }
  return out
}

export function providerCredentialsChanged(
  code: string,
  integration: Pick<POSIntegration, 'credentials' | 'credentials_preview'>,
  formCredentials: POSCredentials,
): boolean {
  const defs = getProviderFieldDefs(code)
  if (!defs) return false
  return defs.some((f) => {
    const formVal = String(formCredentials[f.key] ?? '').trim()
    const stored = getStoredCredentialValue(integration, f.key)
    if (f.sensitive && !formVal) return false
    if (!f.required && !formVal && stored) return true
    if (!formVal) return false
    const normalizedForm =
      f.key === REMARIS_CREDENTIAL_KEYS.apiBaseUrl ? normalizeRemarisApiBaseUrl(formVal) : formVal
    return normalizedForm !== stored
  })
}

export function isCredentialConfigured(
  integration: Pick<POSIntegration, 'credentials' | 'credentials_preview'> | null | undefined,
  key: string,
): boolean {
  if (getStoredCredentialValue(integration, key)) return true
  return integration?.credentials_preview?.configured?.[key] === true
}

export interface PosErrorPayload {
  ok?: boolean
  code?: string
  message?: string
  error?: string
  detail?: string
}

export function resolvePosErrorMessage(
  payload: PosErrorPayload | string | null | undefined,
  t: (key: string) => string,
): string {
  if (!payload) {
    return t('pos.errors.generic')
  }
  if (typeof payload === 'string') {
    return mapRawPosErrorString(payload, t)
  }
  let main = ''
  if (payload.code) {
    const key = `pos.errors.${payload.code}`
    const translated = t(key)
    if (translated !== key) {
      main = translated
    }
  }
  if (!main) {
    const text = payload.message || payload.error
    main = text ? mapRawPosErrorString(text, t) : t('pos.errors.generic')
  }
  if (payload.detail && payload.detail !== main) {
    return `${main} (${payload.detail})`
  }
  return main
}

function mapRawPosErrorString(raw: string, t: (key: string) => string): string {
  const msg = raw.toLowerCase()
  if (msg.includes('remaris seo: http 401') || msg.includes('gm-apikey')) {
    return t('pos.errors.remaris_unauthorized')
  }
  if (msg.includes('remaris seo: http 404')) {
    return t('pos.errors.remaris_not_found')
  }
  if (msg.includes('remaris seo: http 5')) {
    return t('pos.errors.remaris_server_error')
  }
  if (msg.includes('http 401') || msg.includes('unauthorized')) {
    return t('pos.errors.servus_unauthorized')
  }
  if (msg.includes('http 403') || msg.includes('forbidden')) {
    return t('pos.errors.servus_forbidden')
  }
  if (msg.includes('http 404') || msg.includes('not found')) {
    return t('pos.errors.servus_not_found')
  }
  if (msg.includes('http 400') || msg.includes('request is invalid') || msg.includes('invalid')) {
    return t('pos.errors.servus_invalid_request')
  }
  if (msg.includes('http 5') || msg.includes('http 502') || msg.includes('http 503')) {
    return t('pos.errors.servus_server_error')
  }
  if (msg.includes('meni ne sadrži') || msg.includes('menu_empty')) {
    return t('pos.errors.menu_empty')
  }
  if (msg.includes('unsupported protocol scheme') || msg.includes('api_base_url nema host')) {
    return t('pos.errors.remaris_invalid_base_url')
  }
  if (msg.includes('dial tcp') || msg.includes('connection refused') || msg.includes('no such host')) {
    return t('pos.errors.network_unavailable')
  }
  if (msg.includes('timeout') || msg.includes('deadline exceeded')) {
    return t('pos.errors.timeout')
  }
  if (!msg.includes('http ') && !msg.includes('servus getmenu:') && !msg.includes('servus qrorder:')) {
    return raw
  }
  return t('pos.errors.generic')
}
